import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CalendarEvent } from '../types';
import { format } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';

interface QuickEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  onEventUpdated: () => void;
  guards?: Array<{ id: string; name: string; fullName: string; }>;
  updateShift?: (shiftId: string, updates: Partial<CalendarEvent>) => Promise<CalendarEvent | null>;
}

const QuickEditDialog: React.FC<QuickEditDialogProps> = ({
  isOpen,
  onClose,
  event,
  onEventUpdated,
  guards = [],
  updateShift: updateShiftProp
}) => {
  const { t } = useLanguage();
  const updateShift = updateShiftProp;
  const [isLoading, setIsLoading] = useState(false);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<any>(null);

  useEffect(() => {
    if (event && isOpen) {
      setTitle(event.title || '');
      setDescription(event.description || '');
      
      const start = fromLocalISOString(event.start_date);
      const end = fromLocalISOString(event.end_date);
      
      setStartDate(format(start, 'yyyy-MM-dd'));
      setEndDate(format(end, 'yyyy-MM-dd'));
      setStartTime(format(start, 'HH:mm'));
      setEndTime(format(end, 'HH:mm'));
    }
  }, [event, isOpen]);

  const hasTimeChange = () => {
    if (!event) return false;
    
    const originalStart = fromLocalISOString(event.start_date);
    const originalEnd = fromLocalISOString(event.end_date);
    
    const originalStartTime = format(originalStart, 'HH:mm');
    const originalEndTime = format(originalEnd, 'HH:mm');
    const originalStartDate = format(originalStart, 'yyyy-MM-dd');
    const originalEndDate = format(originalEnd, 'yyyy-MM-dd');
    
    return (
      startTime !== originalStartTime || 
      endTime !== originalEndTime ||
      startDate !== originalStartDate ||
      endDate !== originalEndDate
    );
  };

  const handleSave = async () => {
    if (!event) return;

    const newStartDateTime = new Date(`${startDate}T${startTime}:00`);
    const newEndDateTime = new Date(`${endDate}T${endTime}:00`);

    const changes = {
      title,
      description,
      start_date: toLocalISOString(newStartDateTime),
      end_date: toLocalISOString(newEndDateTime),
      // Keep the same guards - this is the key fix
      assigned_guards: event.assigned_guards || []
    };

    // If there are time/date changes, show confirmation
    if (hasTimeChange()) {
      setPendingChanges(changes);
      setShowConfirmation(true);
      return;
    }

    // If no time changes, update directly
    await performUpdate(changes);
  };

  const performUpdate = async (changes: any) => {
    if (!event || !updateShift) return;

    setIsLoading(true);
    try {
      const result = await updateShift(event.id, changes);
      if (result) {
        onEventUpdated();
        onClose();
        setShowConfirmation(false);
        setPendingChanges(null);
      }
    } catch (error) {
      console.error('Error updating shift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmTimeChange = async () => {
    if (pendingChanges) {
      await performUpdate(pendingChanges);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setPendingChanges(null);
    onClose();
  };

  const getGuardNames = () => {
    if (!event?.assigned_guards || event.assigned_guards.length === 0) {
      return t('noGuardsAssigned') || 'No guards assigned';
    }
    
    const guardNames = event.assigned_guards
      .map(guardId => {
        const guard = guards.find(g => g.id === guardId);
        return guard ? guard.fullName || guard.name : null;
      })
      .filter(Boolean);
    
    return guardNames.length > 0 ? guardNames.join(', ') : (t('noGuardsAssigned') || 'No guards assigned');
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('quickEditShift') || 'Quick Edit Shift'}
          </DialogTitle>
        </DialogHeader>

        {showConfirmation ? (
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('confirmTimeChange') || 'You are changing the time/date of this shift. This will affect all assigned guards. Do you want to continue?'}
              </AlertDescription>
            </Alert>
            
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">
                {t('assignedGuards') || 'Assigned Guards'}:
              </p>
              <p className="text-sm text-muted-foreground">
                {getGuardNames()}
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmation(false)}
                disabled={isLoading}
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button 
                onClick={handleConfirmTimeChange}
                disabled={isLoading}
              >
                {isLoading ? (t('saving') || 'Saving...') : (t('confirm') || 'Confirm')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">{t('title') || 'Title'}</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('enterTitle') || 'Enter title'}
              />
            </div>

            <div>
              <Label htmlFor="description">{t('description') || 'Description'}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('enterDescription') || 'Enter description'}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">{t('startDate') || 'Start Date'}</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="startTime">{t('startTime') || 'Start Time'}</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="endDate">{t('endDate') || 'End Date'}</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="endTime">{t('endTime') || 'End Time'}</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">
                {t('assignedGuards') || 'Assigned Guards'}:
              </p>
              <p className="text-sm text-muted-foreground">
                {getGuardNames()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('guardsWillRemainAssigned') || 'Guards will remain assigned to this shift'}
              </p>
            </div>

            {hasTimeChange() && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t('timeChangeWarning') || 'Changing the time will require confirmation and will affect all assigned guards.'}
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                {t('cancel') || 'Cancel'}
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                {isLoading ? (t('saving') || 'Saving...') : (t('save') || 'Save')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickEditDialog;
